import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, BarChart3, Presentation, Shield, ArrowRight, Check, X } from 'lucide-react';
import SEO from '../components/SEO';

const GUMROAD_COMPLETE = 'https://shapley3.gumroad.com/l/founder-toolkit-complete';
const GUMROAD_STARTER = 'https://shapley3.gumroad.com/l/founder-toolkit-starter';

const templates = [
  { name: 'Business Plan', pages: 18, tier: 'starter' },
  { name: 'One-Pager', pages: 5, tier: 'starter' },
  { name: 'Business Model Canvas', pages: 5, tier: 'starter' },
  { name: 'Product Requirements Doc', pages: 10, tier: 'complete' },
  { name: 'Customer Interview Toolkit', pages: 10, tier: 'complete' },
  { name: 'User Persona Template', pages: 7, tier: 'complete' },
  { name: 'Press Release Template', pages: 6, tier: 'complete' },
  { name: 'Partnership Agreement', pages: 7, tier: 'complete' },
];

const spreadsheets = [
  'Financial Projections', 'Metrics Dashboard', 'Competitive Analysis',
  'GTM Plan', 'Feature Prioritization', 'OKR Planning',
  'Product Roadmap', 'Customer Interview Tracker', 'Hiring Plan',
  'Risk Register', 'Business Model Canvas', 'Vesting Calculator',
];

const faqs = [
  {
    q: 'What format are the templates in?',
    a: 'PDFs with guide + worksheet format, Excel spreadsheets (.xlsx) with working formulas, and PowerPoint presentations (.pptx). They work on Mac, Windows, and Google Workspace.',
  },
  {
    q: 'Can I edit the PDF templates?',
    a: 'The PDFs are designed as reference guides with printable worksheets. The Excel spreadsheets and PowerPoint decks are fully editable — just plug in your own numbers and content.',
  },
  {
    q: "What's the difference between Starter and Complete?",
    a: 'Starter includes the 3 most essential PDF templates (Business Plan, One-Pager, Business Model Canvas). Complete adds 5 more PDF templates, 12 working Excel spreadsheets, and 2 presentation decks.',
  },
  {
    q: 'Is there a refund policy?',
    a: "Yes — 30-day money-back guarantee on both tiers. If the templates don't save you time, you get a full refund.",
  },
  {
    q: 'Will I get updates?',
    a: 'Yes. When we update or add templates, existing customers get access to the new versions at no extra cost.',
  },
];

const FounderToolkit = () => {
  return (
    <>
      <SEO
        title="The Founder Toolkit"
        description="8 professional PDF templates, 12 Excel spreadsheets, and 2 presentation decks. Everything a founder needs from first idea to Series A."
        path="/founder-toolkit"
      />

      <div>
        {/* ─── HERO ─── */}
        <div className="relative min-h-[85vh] flex items-center pt-24 overflow-hidden">
          <div className="absolute w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-[120px] -top-40 -right-40" />
          <div className="absolute w-[400px] h-[400px] bg-indigo-500/10 rounded-full blur-[120px] bottom-0 -left-40" />

          <div className="max-w-7xl mx-auto px-6 text-center">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-cyan-400 mb-4">
              Built by founders, for founders
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight max-w-4xl mx-auto">
              Stop Googling templates.{' '}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-indigo-400">
                Start building your startup.
              </span>
            </h1>
            <p className="mt-6 text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed">
              Professional PDF templates, working Excel spreadsheets, and presentation decks.
              Everything you need from first idea to Series A — in one toolkit.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 justify-center">
              <a
                href={GUMROAD_COMPLETE}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-cyan-500 to-indigo-500 rounded-xl px-8 py-4 font-display font-semibold text-white shadow-[0_0_30px_rgba(6,182,212,0.35)] hover:shadow-[0_0_40px_rgba(6,182,212,0.5)] transition-all duration-300"
              >
                Get the Complete Toolkit — $79
              </a>
              <a
                href={GUMROAD_STARTER}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/[0.06] backdrop-blur border border-white/[0.1] rounded-xl px-8 py-4 font-display font-semibold text-white hover:bg-white/[0.1] transition-all duration-300"
              >
                Start with 3 templates — $29+
              </a>
            </div>
          </div>
        </div>

        {/* ─── STATS ─── */}
        <div className="py-16 border-y border-white/[0.04]">
          <div className="max-w-5xl mx-auto px-6 grid grid-cols-2 sm:grid-cols-4 gap-8 text-center">
            {[
              { num: '8', label: 'PDF Templates' },
              { num: '12', label: 'Excel Spreadsheets' },
              { num: '2', label: 'Presentation Decks' },
              { num: '68', label: 'Pages of Content' },
            ].map((s) => (
              <div key={s.label}>
                <div className="font-display text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-indigo-400">
                  {s.num}
                </div>
                <div className="text-sm text-slate-500 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ─── WHAT'S INSIDE ─── */}
        <div className="py-24">
          <div className="max-w-7xl mx-auto px-6">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-cyan-400 mb-3">
              What's inside
            </p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-4">
              Three types of tools. One complete system.
            </h2>
            <p className="text-lg text-slate-400 max-w-xl mb-16">
              Every template shows you what great looks like, then gives you blank worksheets to build your own.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  icon: <FileText className="w-6 h-6 text-cyan-400" />,
                  title: '8 PDF Templates',
                  desc: 'Guide + worksheet format. Each one starts with a filled-in example so you see what "good" looks like, followed by blank worksheets to create your own.',
                  color: 'cyan',
                },
                {
                  icon: <BarChart3 className="w-6 h-6 text-indigo-400" />,
                  title: '12 Excel Spreadsheets',
                  desc: 'Working formulas, not empty cells. Financial projections, metrics dashboards, competitive analysis, GTM plans, and more. Plug in your numbers and go.',
                  color: 'indigo',
                },
                {
                  icon: <Presentation className="w-6 h-6 text-primary-400" />,
                  title: '2 Presentation Decks',
                  desc: 'Board meeting template and sales deck — professionally designed with placeholder slides ready for your content.',
                  color: 'sky',
                },
              ].map((card) => (
                <div
                  key={card.title}
                  className="bg-white/[0.03] backdrop-blur border border-white/[0.06] rounded-2xl p-8 hover:border-cyan-500/30 transition-all duration-300"
                >
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 ${
                      card.color === 'cyan'
                        ? 'bg-cyan-500/10'
                        : card.color === 'indigo'
                        ? 'bg-indigo-500/10'
                        : 'bg-primary-500/10'
                    }`}
                  >
                    {card.icon}
                  </div>
                  <h3 className="font-display text-lg font-bold text-white mb-2">{card.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{card.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ─── FULL TEMPLATE LIST ─── */}
        <div className="py-24 bg-surface-1/50">
          <div className="max-w-7xl mx-auto px-6">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-cyan-400 mb-3">
              The templates
            </p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-4">
              Every document a startup needs
            </h2>
            <p className="text-lg text-slate-400 max-w-xl mb-12">
              Each PDF includes a guide section showing best-in-class examples, plus blank worksheets to fill in yourself.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {templates.map((t) => (
                <div
                  key={t.name}
                  className="flex items-center gap-4 bg-white/[0.03] border border-white/[0.06] rounded-xl px-5 py-4"
                >
                  <div
                    className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                      t.tier === 'starter' ? 'bg-cyan-400' : 'bg-indigo-400'
                    }`}
                  />
                  <span className="font-display font-semibold text-white text-sm">{t.name}</span>
                  <span className="ml-auto text-slate-500 text-xs">{t.pages} pages</span>
                </div>
              ))}
            </div>

            <h3 className="font-display text-lg font-bold text-white mb-4 mt-12">
              Plus 12 working Excel spreadsheets
            </h3>
            <div className="flex flex-wrap gap-3">
              {spreadsheets.map((s) => (
                <span
                  key={s}
                  className="px-4 py-2 bg-white/[0.03] border border-white/[0.06] rounded-full text-slate-400 text-xs font-mono"
                >
                  {s}
                </span>
              ))}
            </div>

            {/* Guarantee */}
            <div className="mt-12 flex items-center gap-4 bg-cyan-500/[0.05] border border-cyan-500/20 rounded-2xl p-6 max-w-2xl">
              <Shield className="w-8 h-8 text-cyan-400 flex-shrink-0" />
              <p className="text-slate-400 text-sm">
                <span className="text-white font-semibold">30-day money-back guarantee.</span>{' '}
                If these templates don't save you hours of work, get a full refund. No questions asked.
              </p>
            </div>
          </div>
        </div>

        {/* ─── PRICING ─── */}
        <div className="py-24">
          <div className="max-w-7xl mx-auto px-6">
            <div className="bg-surface-1/80 backdrop-blur border border-white/[0.04] rounded-3xl p-8 sm:p-16">
              <div className="text-center mb-12">
                <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-cyan-400 mb-3">
                  Pricing
                </p>
                <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                  Choose your toolkit
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
                {/* Starter */}
                <div className="bg-surface-0 border border-white/[0.06] rounded-2xl p-8 hover:border-white/[0.12] transition-all duration-300">
                  <h3 className="font-display text-xl font-bold text-white mb-2">Starter</h3>
                  <p className="text-slate-400 text-sm mb-6">The 3 core templates every founder needs</p>
                  <div className="font-display text-5xl font-bold text-white mb-1">
                    <span className="text-xl align-top">$</span>29
                    <span className="text-base font-normal text-slate-500">+</span>
                  </div>
                  <p className="text-slate-500 text-xs mb-8">Pay what you want (min $29)</p>
                  <ul className="space-y-3 mb-8">
                    {[
                      { text: 'Business Plan Template (18 pages)', on: true },
                      { text: 'One-Pager Template (5 pages)', on: true },
                      { text: 'Business Model Canvas (5 pages)', on: true },
                      { text: '5 more PDF templates', on: false },
                      { text: '12 Excel spreadsheets', on: false },
                      { text: '2 presentation decks', on: false },
                    ].map((f) => (
                      <li key={f.text} className={`flex items-center gap-3 text-sm ${f.on ? 'text-slate-400' : 'text-slate-600'}`}>
                        {f.on ? (
                          <Check className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                        ) : (
                          <X className="w-4 h-4 text-slate-600 flex-shrink-0" />
                        )}
                        {f.text}
                      </li>
                    ))}
                  </ul>
                  <a
                    href={GUMROAD_STARTER}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-center bg-white/[0.06] border border-white/[0.1] rounded-xl py-3.5 font-display font-semibold text-white hover:bg-white/[0.1] transition-all duration-300"
                  >
                    Get Starter
                  </a>
                </div>

                {/* Complete */}
                <div className="relative bg-surface-0 border border-cyan-500/40 rounded-2xl p-8 shadow-[0_0_40px_rgba(6,182,212,0.1)]">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-cyan-400 to-indigo-400 text-[0.65rem] font-bold uppercase tracking-wider text-surface-0 px-4 py-1 rounded-full">
                    Best Value
                  </div>
                  <h3 className="font-display text-xl font-bold text-white mb-2">Complete</h3>
                  <p className="text-slate-400 text-sm mb-6">Everything from first idea to Series A</p>
                  <div className="font-display text-5xl font-bold text-white mb-1">
                    <span className="text-xl align-top">$</span>79
                  </div>
                  <p className="text-slate-500 text-xs mb-8">One-time payment, lifetime access</p>
                  <ul className="space-y-3 mb-8">
                    {[
                      'All 8 PDF templates (68 pages)',
                      '12 Excel spreadsheets with formulas',
                      '2 presentation decks',
                      'Financial projections model',
                      'Metrics dashboard',
                      'Competitive analysis framework',
                    ].map((f) => (
                      <li key={f} className="flex items-center gap-3 text-sm text-slate-400">
                        <Check className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <a
                    href={GUMROAD_COMPLETE}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-center bg-gradient-to-r from-cyan-500 to-indigo-500 rounded-xl py-3.5 font-display font-semibold text-white shadow-[0_0_20px_rgba(6,182,212,0.25)] hover:shadow-[0_0_30px_rgba(6,182,212,0.4)] transition-all duration-300"
                  >
                    Get Complete Toolkit
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ─── FAQ ─── */}
        <div className="py-24 bg-surface-1/50">
          <div className="max-w-7xl mx-auto px-6">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-cyan-400 mb-3">
              FAQ
            </p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-12">
              Common questions
            </h2>

            <div className="max-w-2xl space-y-0 divide-y divide-white/[0.06]">
              {faqs.map((faq) => (
                <div key={faq.q} className="py-6">
                  <h3 className="font-display font-semibold text-white mb-2">{faq.q}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ─── FINAL CTA ─── */}
        <div className="py-24 text-center">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Ready to{' '}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-indigo-400">
                build something?
              </span>
            </h2>
            <p className="text-lg text-slate-400 max-w-lg mx-auto mb-10">
              Stop spending days on documents. Start spending days on your startup.
            </p>
            <a
              href={GUMROAD_COMPLETE}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-indigo-500 rounded-xl px-8 py-4 font-display font-semibold text-white shadow-[0_0_30px_rgba(6,182,212,0.35)] hover:shadow-[0_0_40px_rgba(6,182,212,0.5)] transition-all duration-300"
            >
              Get the Complete Toolkit — $79
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default FounderToolkit;
